import {useLingui, Trans} from '@lingui/react/macro';
import {useCallback} from 'react';
import {StyleSheet, View, Text, Pressable} from 'react-native';
import {Svg, Path, Rect, Defs, LinearGradient, Stop} from 'react-native-svg';
import {useSelector} from 'react-redux';
import {openPage} from 'utils/mediafire';
import {bytesize} from 'utils/common/data';
import {vectors} from 'config/styles';

import * as app from 'store/slices/app';

export type StorageUpsellAction = 'remind' | 'manage' | 'upload';

export interface StorageUpsellProps {
  exit: () => void,
  action?: StorageUpsellAction,
  needed?: number,
  limit?: number,
}

export function ModalStorageUpsell(props: StorageUpsellProps) {
  const {t} = useLingui();
  const action = props.action ?? 'remind';
  const isCritical = action !== 'remind';
  const limits = useSelector(app.selectors.getLimits);
  const total = limits?.storage.total ?? 0;
  const used = limits?.storage.used ?? 0;
  const percent = total > 0
    ? Math.max(0, Math.min(1, used / total))
    : 0.94;
  const capPercent = 1 - percent;
  const neededLabel = bytesize(props.needed ?? 0, 0);
  const limitLabel = bytesize(props.limit ?? total, 0);

  const gotoUpgrade = useCallback(() => {
    openPage('/upgrade/');
  }, []);

  return (
    <View style={styles.center}>
      <View style={styles.popup}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t`Close`}
          onPress={props.exit}
          style={({hovered}: any) => [styles.close, hovered && styles.closeHover]}>
          <Svg viewBox="0 0 14 14" width={14} height={14}>
            <Path d={vectors.icons.close} fill={colors.closeIcon}/>
          </Svg>
        </Pressable>

        <Text style={styles.title}>
          {action === 'manage' && t`You're out of storage space!`}
          {action === 'upload' && t`Not enough space for upload`}
          {action === 'remind' && t`You're almost out of storage space!`}
        </Text>

        <View style={styles.barOuter}>
          <View style={styles.barWrapper}>
            <View style={styles.barContainer}>
              <Svg
                style={StyleSheet.absoluteFill as any}
                width="100%"
                height="100%"
                viewBox="0 0 100 16"
                preserveAspectRatio="none">
                <Defs>
                  <LinearGradient id="mfStorageBar" x1="0" y1="0" x2="1" y2="0">
                    <Stop offset={0} stopColor="#00F0B6"/>
                    <Stop offset={0.25} stopColor="#00B6F0"/>
                    <Stop offset={0.5} stopColor="#0070F0"/>
                    <Stop offset={0.75} stopColor="#8F01FF"/>
                    <Stop offset={1} stopColor="#8F01FF"/>
                  </LinearGradient>
                </Defs>
                <Rect x={0} y={0} width={100} height={16} fill="url(#mfStorageBar)"/>
              </Svg>
              {isCritical
                ? <View style={[styles.barCap, styles.barCapCritical]}/>
                : <View style={[styles.barCap, {width: `${capPercent * 100}%` as any}]}/>
              }
            </View>
            <View style={[
              styles.barPointer,
              isCritical
                ? styles.barPointerCritical
                : {right: `${capPercent * 100}%` as any},
            ]}>
              <View style={styles.barPointerArrow}/>
            </View>
          </View>
        </View>

        <Text style={styles.body}>
          {action === 'manage' &&
            <Trans>
              You don't have enough available space to complete this action. Your current limit is{' '}
              <Text style={styles.bold}>{limitLabel}</Text> and you need at least another{' '}
              <Text style={styles.bold}>{neededLabel}</Text> of space. Upgrade your account to get{' '}
              <Text style={styles.bold}>more storage space</Text>,{' '}
              <Text style={styles.bold}>ad-free pages</Text> and{' '}
              <Text style={styles.bold}>more</Text>:
            </Trans>
          }
          {action === 'upload' &&
            <Trans>
              You don't have enough storage space to complete this upload. Upgrade your account to get{' '}
              <Text style={styles.bold}>more storage space</Text>,{' '}
              <Text style={styles.bold}>ad-free pages</Text> and{' '}
              <Text style={styles.bold}>more</Text>:
            </Trans>
          }
          {action === 'remind' &&
            <Trans>
              Did you know it's easy to get more MediaFire storage? Upgrade your account to get{' '}
              <Text style={styles.bold}>more storage space</Text>,{' '}
              <Text style={styles.bold}>ad-free pages</Text> and{' '}
              <Text style={styles.bold}>more</Text>:
            </Trans>
          }
        </Text>

        <View style={styles.benefits}>
          <View style={styles.benefit}>
            <IconDirectDownloads/>
            <Text style={styles.benefitText}>
              {t`Download files directly without a download page.`}
            </Text>
          </View>
          <View style={styles.benefitDivider}/>
          <View style={styles.benefit}>
            <IconStorageSafe/>
            <Text style={styles.benefitText}>
              {t`Reliable, long-term storage to keep your files safe.`}
            </Text>
          </View>
          <View style={styles.benefitDivider}/>
          <View style={styles.benefit}>
            <IconPasswordProtected/>
            <Text style={styles.benefitText}>
              {t`Secure sharing with password protected files.`}
            </Text>
          </View>
        </View>

        <View style={styles.buttons}>
          <Pressable
            accessibilityRole="button"
            onPress={props.exit}
            style={({hovered}: any) => [
              styles.btn,
              styles.btnLater,
              hovered && styles.btnLaterHover,
            ]}>
            <Text style={styles.btnLaterText}>
              {t`Upgrade later`.toUpperCase()}
            </Text>
          </Pressable>
          <View style={styles.btnSpacer}/>
          <Pressable
            accessibilityRole="button"
            onPress={gotoUpgrade}
            style={({hovered}: any) => [
              styles.btn,
              styles.btnNow,
              hovered && styles.btnNowHover,
            ]}>
            <Text style={styles.btnNowText}>
              {t`Upgrade now!`.toUpperCase()}
            </Text>
            <View style={styles.btnNowArrow}>
              <Svg viewBox="0 0 24 24" width={18} height={18}>
                <Path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="#fff"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </Svg>
            </View>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function IconDirectDownloads() {
  return (
    <Svg width={80} height={80} viewBox="0 0 80 80" fill="none">
      <Rect width={80} height={80} rx={40} fill="#F4F4F5"/>
      <Path d="M66.007 40L61 45v-3H15.75A1.75 1.75 0 0114 40.25v-.5A1.75 1.75 0 0115.75 38H61v-3l5.007 5z" fill="#FA6F50"/>
      <Path d="M62 37.414L64.59 40 62 42.586V41H15.751a.751.751 0 01-.751-.75v-.5a.751.751 0 01.751-.75H62v-1.586zM61 35v3H15.75A1.75 1.75 0 0014 39.75v.5A1.75 1.75 0 0015.75 42H61v3l5.007-5L61 35z" fill="#D55E44"/>
      <Path d="M23.863 22.17A5.52 5.52 0 0022.5 22v-1c.543 0 1.084.067 1.61.2l-.247.97zM36.07 59.81a9.34 9.34 0 01-1.58.19c-.547 0-1.091-.07-1.62-.21l.25-.96c.453.106.915.163 1.38.17a5.38 5.38 0 001.33-.16l.24.97zm4.15-3.21a6.553 6.553 0 01-2.18 2.35l-.54-.84a5.666 5.666 0 001.84-1.99l.88.48zm-8.76 1.49l-.55.83a6.521 6.521 0 01-2.15-2.37l.88-.47a5.662 5.662 0 001.82 2.01zm23.94-.04l-.56.82a6.532 6.532 0 01-2.12-2.4l.89-.46a5.59 5.59 0 001.79 2.04zM41 51.5v2c0 .359-.03.717-.09 1.07l-.98-.17c.044-.298.067-.599.07-.9v-2h1zm-11.93 2.85l-.99.16A6.764 6.764 0 0128 53.5v-2.05h1v2.05c-.002.285.021.57.07.85zm23.99-.06l-.99.14a5.814 5.814 0 01-.07-.93v-2.13h1v2.13c0 .264.02.529.06.79zM41 46.52v2.99h-1v-2.99h1zm-12-.05v2.99h-1v-2.99h1zm24-.08v2.99h-1v-2.99h1zM41 31.58v2.99h-1v-2.99h1zm-12-.05v2.99h-1v-2.99h1zm24-.08v2.99h-1v-2.99h1zm-11.94-4.77c-.04.271-.06.546-.06.82v2.09h-1V27.5a6.248 6.248 0 01.07-.97l.99.15zM29 27.5v2.04h-1V27.5a5.092 5.092 0 00-.07-.87l.99-.16c.051.341.078.685.08 1.03zm24 0v1.96h-1V27.5a5.968 5.968 0 00-.08-.94l.99-.17c.06.367.09.738.09 1.11zm-9.57-4.56a5.551 5.551 0 00-1.81 2.01l-.88-.46a6.617 6.617 0 012.13-2.39l.56.84zm-15.2 1.5l-.88.47a5.47 5.47 0 00-1.82-2l.55-.84a6.521 6.521 0 012.15 2.37zm23.96-.08l-.87.49a5.491 5.491 0 00-1.85-1.98l.54-.84a6.494 6.494 0 012.18 2.33zm-4.16-3.18l-.23.97a5.643 5.643 0 00-2.71.03l-.26-.96a6.541 6.541 0 013.2-.04zM58.5 59c-.46 0-.917-.057-1.363-.17l-.247.969c.526.134 1.067.2 1.61.2V59z" fill="#7A838E"/>
      <Path d="M21.5 23.5a2 2 0 100-4 2 2 0 000 4zM60 61a2 2 0 100-4 2 2 0 000 4z" fill="#7A838E"/>
    </Svg>
  );
}

function IconStorageSafe() {
  return (
    <Svg width={80} height={80} viewBox="0 0 80 80" fill="none">
      <Rect width={80} height={80} rx={40} fill="#F4F4F5"/>
      <Path d="M56.081 23H23.919A2.919 2.919 0 0021 25.919v28.162A2.919 2.919 0 0023.919 57h32.162A2.919 2.919 0 0059 54.081V25.919A2.919 2.919 0 0056.081 23z" fill="#4BA3FD"/>
      <Path d="M56.081 24A1.921 1.921 0 0158 25.919v28.162A1.921 1.921 0 0156.081 56H23.919A1.921 1.921 0 0122 54.081V25.919A1.921 1.921 0 0123.919 24h32.162zm0-1H23.919A2.919 2.919 0 0021 25.919v28.162A2.919 2.919 0 0023.919 57h32.162A2.919 2.919 0 0059 54.081V25.919A2.919 2.919 0 0056.081 23z" fill="#2E8AE6"/>
      <Path d="M26.5 32v-.5h-1v-4h29v25h-29v-4h1v-4h-1v-9h1V32z" fill="#4BA3FD" stroke="#1761C0"/>
      <Path d="M46.475 39.49h-1a5.49 5.49 0 00-1.25-3.015l.7-.7a6.294 6.294 0 00-.71-.705l-.7.705a5.447 5.447 0 00-3.015-1.25v-1c-.165-.01-.33-.025-.5-.025-.17 0-.335.015-.5.025v1a5.447 5.447 0 00-3.015 1.245l-.7-.705a6.294 6.294 0 00-.71.705l.7.7a5.49 5.49 0 00-1.25 3.015h-1c-.01.17-.025.34-.025.51 0 .17.015.33.025.49h1a5.44 5.44 0 001.245 3.025l-.705.7c.216.255.452.492.705.71l.7-.7a5.484 5.484 0 003.025 1.25v1c.165.01.33.025.5.025.17 0 .335-.015.5-.025v-1a5.483 5.483 0 003.025-1.25l.7.7c.254-.218.49-.455.705-.71l-.705-.7a5.44 5.44 0 001.245-3.025h1c.01-.16.025-.325.025-.49 0-.165-.005-.335-.015-.505zM40 44.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9z" fill="#fff"/>
      <Path d="M43 40a3 3 0 11-3-3 2.95 2.95 0 011.74.56l-1.385 1.38a.5.5 0 10.705.705l1.385-1.38c.362.506.556 1.113.555 1.735z" fill="#fff"/>
      <Path opacity={0.7} d="M51 44.092v-8.184a1.5 1.5 0 10-1 0v8.184a1.5 1.5 0 101 0z" fill="#0045A6"/>
    </Svg>
  );
}

function IconPasswordProtected() {
  return (
    <Svg width={80} height={80} viewBox="0 0 80 80" fill="none">
      <Rect width={80} height={80} rx={40} fill="#F4F4F5"/>
      <Path d="M57 35v25H23V20h23l9.07 9.07C57 31 57 35 57 35z" fill="#fff"/>
      <Path d="M46 31V20l9.073 9.073C57 31 57 35 57 35s-2-4-6-4h-5z" fill="#E8E9ED"/>
      <Path d="M55.07 29.07L46 20H23v40h34V35s0-4-1.93-5.93zM47 22.41l7.37 7.37a4.9 4.9 0 011.04 1.8A7 7 0 0051 30h-4v-7.59zM56 59H24V21h22v10h5a6.565 6.565 0 015 2.55V59z" fill="#B2B7C4"/>
      <Path opacity={0.2} d="M56.98 34.98A8.89 8.89 0 0051 33h-5v-2h5c3.83 0 5.83 3.67 5.98 3.98z" fill="#6B7587"/>
      <Path d="M48.574 46H31.426A2.426 2.426 0 0029 48.426v3.148A2.426 2.426 0 0031.426 54h17.148A2.426 2.426 0 0051 51.574v-3.148A2.426 2.426 0 0048.574 46z" fill="#E8E9ED"/>
      <Path d="M48.574 47A1.427 1.427 0 0150 48.426v3.148A1.427 1.427 0 0148.574 53H31.426A1.427 1.427 0 0130 51.574v-3.148A1.427 1.427 0 0131.426 47h17.148zm0-1H31.426A2.426 2.426 0 0029 48.426v3.148A2.426 2.426 0 0031.426 54h17.148A2.426 2.426 0 0051 51.574v-3.148A2.426 2.426 0 0048.574 46z" fill="#B2B7C4"/>
      <Path d="M36 51a1 1 0 100-2 1 1 0 000 2zM40 51a1 1 0 100-2 1 1 0 000 2zM44 51a1 1 0 100-2 1 1 0 000 2z" fill="#495262"/>
      <Path d="M43.422 35h-6.844c-.872 0-1.578.706-1.578 1.578v3.844c0 .872.706 1.578 1.578 1.578h6.844c.872 0 1.578-.706 1.578-1.578v-3.844c0-.872-.706-1.578-1.578-1.578z" fill="#FFCE3B"/>
      <Path d="M43.42 35H43v-1a3 3 0 00-6 0v1h-.42A1.583 1.583 0 0035 36.58v3.84A1.583 1.583 0 0036.58 42h6.84A1.583 1.583 0 0045 40.42v-3.84A1.583 1.583 0 0043.42 35zM38 34a2 2 0 014 0v1h-4v-1zm6 6.42a.58.58 0 01-.58.58h-6.84a.58.58 0 01-.58-.58v-3.84a.58.58 0 01.58-.58h6.84a.58.58 0 01.58.58v3.84z" fill="#CCA52E"/>
    </Svg>
  );
}

const colors = {
  popupBg: '#ffffff',
  title: '#000000',
  body: '#000000',
  divider: '#E9EAEB',
  barCap: '#C1C3C7',
  barCapCritical: '#FF5631',
  barPointer: '#000000',
  btnPrimaryFill: '#0070F0',
  btnPrimaryText: '#ffffff',
  btnSecondaryFill: '#E9EAEB',
  btnSecondaryText: '#575B65',
  closeIcon: '#575B65',
  closeHover: 'rgba(0,0,0,0.06)',
};

const styles = StyleSheet.create({
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: 20,
  },
  popup: {
    width: 708,
    maxWidth: '100%',
    backgroundColor: colors.popupBg,
    borderRadius: 5,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.35,
    shadowRadius: 10,
  },
  close: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    opacity: 0.5,
  },
  closeHover: {
    opacity: 1,
    backgroundColor: colors.closeHover,
  },

  title: {
    fontSize: 26,
    fontWeight: '700',
    color: colors.title,
    textAlign: 'center',
    marginTop: 21,
    marginBottom: 24,
    lineHeight: 36,
  },

  barOuter: {
    paddingHorizontal: 15,
    paddingVertical: 24,
    marginBottom: 24,
  },
  barWrapper: {
    position: 'relative',
  },
  barContainer: {
    height: 16,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  barCap: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.barCap,
    borderLeftWidth: 1,
    borderLeftColor: '#fff',
  },
  barCapCritical: {
    width: 40,
    backgroundColor: colors.barCapCritical,
  },
  barPointer: {
    position: 'absolute',
    top: -20,
    width: 20,
    height: 20,
    marginRight: -10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  barPointerCritical: {
    right: 10,
    marginRight: 0,
  },
  barPointerArrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    borderTopWidth: 5,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: colors.barPointer,
    marginTop: 3,
  },

  body: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.body,
    textAlign: 'center',
    paddingHorizontal: 15,
    marginBottom: 40,
  },
  bold: {
    fontWeight: '700',
  },

  benefits: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginBottom: 40,
  },
  benefit: {
    flexBasis: '33%',
    paddingHorizontal: 15,
    alignItems: 'center',
  },
  benefitDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: colors.divider,
  },
  benefitText: {
    marginTop: 10,
    fontSize: 14,
    lineHeight: 20,
    color: colors.body,
    textAlign: 'center',
  },

  buttons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  btnSpacer: {
    width: 10,
  },
  btn: {
    minHeight: 36,
    paddingHorizontal: 15,
    paddingVertical: 6,
    borderRadius: 3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnLater: {
    backgroundColor: colors.btnSecondaryFill,
  },
  btnLaterHover: {
    opacity: 0.85,
  },
  btnLaterText: {
    color: colors.btnSecondaryText,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  btnNow: {
    backgroundColor: colors.btnPrimaryFill,
  },
  btnNowHover: {
    opacity: 0.9,
  },
  btnNowText: {
    color: colors.btnPrimaryText,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  btnNowArrow: {
    marginLeft: 8,
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
